import { useEffect, useState } from 'react';
import API from '../api';

// ── 공개범위 설정 모달 ──────────────────────────────────
// - 계정관리(AdminUsers) 화면 "조직구성" 옆 "🎯 공개범위 설정" 버튼을 누르면 열립니다.
// - 최고관리자만 접근하는 화면이므로 이 컴포넌트 자체에는 별도 권한 체크가 없고,
//   백엔드(routes/disclosure.js)에서 isSuperAdmin으로 막아둡니다.
// - 공개구분: "전체공개", "임직원공개" 등 이름을 직접 입력해서 추가합니다.
// - 각 공개구분을 펼치면 회원구분/부서구분/직급구분 중 실제 대상을 다중 선택으로 지정합니다.
//
// ── 저장 위치 ──
//   AdminUsers.js와 같은 폴더(frontend/src/pages/)에 파일명을 DisclosureScopeModal.js로 저장해주세요.
// 회원구분: 사내임직원 / 협력사 / 개인관리 (계정관리에서 회원별로 지정하는 member_type 값과 동일).
// "개인관리"도 다른 두 항목과 마찬가지로 별도 개별설정 없이 단순 체크박스로만 동작합니다
// (예전에는 특정 회원을 직접 검색해서 지정하는 별도 섹션이 있었지만, 이제는 계정관리에서
// 회원구분을 "개인관리"로 지정해두면 이 체크박스 하나로 그 회원들 전체가 매칭됩니다).
const MEMBER_TYPE_OPTIONS = [
  { value: 'staff',      label: '사내임직원' },
  { value: 'partner',    label: '협력사' },
  { value: 'individual', label: '개인관리' },
];

export default function DisclosureScopeModal({ onClose }) {
  const [scopes, setScopes]           = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [newName, setNewName]         = useState('');
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [draftTargets, setDraftTargets] = useState([]); // [{target_type, target_value}]
  const [savingTargets, setSavingTargets] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName]   = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([API.get('/disclosure'), API.get('/org')])
      .then(([r1, r2]) => {
        setScopes(r1.data.scopes || []);
        setDepartments(r2.data.departments || []);
        setPositions(r2.data.positions || []);
      })
      .catch(() => setMsg('불러오기 실패'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await API.post('/disclosure', { name });
      setNewName('');
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '추가 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}"을(를) 삭제하시겠습니까?`)) return;
    try {
      await API.delete(`/disclosure/${id}`);
      if (expandedId === id) setExpandedId(null);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  // 기본값 버튼(공개구분명 좌측): 클릭하면 설정/해제를 토글합니다.
  // 설정되면 초록색, 해제되면 흰색 배경으로 표시됩니다.
  const toggleDefault = async (scope) => {
    try {
      await API.patch(`/disclosure/${scope.id}/default`, { value: !scope.is_default });
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '기본값 설정 중 오류가 발생했습니다.');
    }
  };

  // 순서 변경(▲▼): 화면에서 먼저 순서를 바꾼 뒤 전체 순서를 서버에 저장합니다.
  const handleMove = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= scopes.length) return;
    const next = [...scopes];
    [next[index], next[target]] = [next[target], next[index]];
    setScopes(next);
    try {
      await API.patch('/disclosure/reorder', { ids: next.map(s => s.id) });
    } catch (err) {
      setMsg(err.response?.data?.message || '순서 변경 중 오류가 발생했습니다.');
      load();
    }
  };

  const startEdit = (scope) => { setMsg(''); setEditingId(scope.id); setEditName(scope.name); };
  const saveEdit = async (id) => {
    const name = editName.trim();
    if (!name) { setMsg('공개구분명을 입력해주세요.'); return; }
    try {
      await API.patch(`/disclosure/${id}`, { name });
      setEditingId(null);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '수정 중 오류가 발생했습니다.');
    }
  };

  const toggleExpand = (scope) => {
    if (expandedId === scope.id) { setExpandedId(null); return; }
    setMsg('');
    setExpandedId(scope.id);
    setDraftTargets(scope.targets || []);
  };

  const isChecked = (type, value) =>
    draftTargets.some(t => t.target_type === type && t.target_value === value);

  const toggleTarget = (type, value) => {
    setDraftTargets(prev => {
      const exists = prev.some(t => t.target_type === type && t.target_value === value);
      return exists
        ? prev.filter(t => !(t.target_type === type && t.target_value === value))
        : [...prev, { target_type: type, target_value: value }];
    });
  };

  const saveTargets = async (scopeId) => {
    setSavingTargets(true);
    try {
      await API.put(`/disclosure/${scopeId}/targets`, { targets: draftTargets });
      setExpandedId(null);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '공개범위 저장 중 오류가 발생했습니다.');
    } finally {
      setSavingTargets(false);
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:560, maxWidth:'92vw', maxHeight:'86vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🎯 공개범위 설정</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ padding:'16px 24px' }}>
          <div style={{ display:'flex', gap:8, marginBottom:14 }}>
            <input value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="새 공개구분명 입력 (예: 전체공개, 임직원공개)"
              style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                fontSize:14, boxSizing:'border-box', outline:'none' }} />
            <button onClick={handleAdd} disabled={saving || !newName.trim()}
              style={{ padding:'9px 16px', background: newName.trim() ? '#0F6E56' : '#ccc', color:'#fff',
                border:'none', borderRadius:8, fontSize:13, fontWeight:600,
                cursor: newName.trim() ? 'pointer' : 'not-allowed', whiteSpace:'nowrap' }}>
              + 추가
            </button>
          </div>

          {msg && <div style={{ fontSize:13, color:'#dc2626', marginBottom:10 }}>{msg}</div>}

          <div style={{ border:'1px solid #eee', borderRadius:8, maxHeight:420, overflowY:'auto' }}>
            {loading ? (
              <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>불러오는 중...</div>
            ) : scopes.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>
                등록된 공개구분이 없습니다.
              </div>
            ) : (
              scopes.map((scope, idx) => (
                <div key={scope.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    padding:'10px 14px', gap:8 }}>

                    {editingId === scope.id ? (
                      <>
                        <input value={editName} onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveEdit(scope.id)}
                          autoFocus
                          style={{ flex:1, padding:'6px 8px', border:'1px solid #0F6E56', borderRadius:6,
                            fontSize:13, outline:'none', boxSizing:'border-box' }} />
                        <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                          <button onClick={() => saveEdit(scope.id)}
                            style={{ background:'#0F6E56', border:'none', color:'#fff', fontSize:12,
                              padding:'4px 10px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                            저장
                          </button>
                          <button onClick={() => setEditingId(null)}
                            style={{ background:'none', border:'none', color:'#888', fontSize:12, cursor:'pointer' }}>
                            취소
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ display:'flex', alignItems:'center', gap:2, flexShrink:0 }}>
                          <button onClick={() => handleMove(idx, -1)} disabled={idx === 0}
                            style={{ background:'none', border:'none', fontSize:12, padding:'2px 4px',
                              color: idx === 0 ? '#ddd' : '#666', cursor: idx === 0 ? 'default' : 'pointer' }}>
                            ▲
                          </button>
                          <button onClick={() => handleMove(idx, 1)} disabled={idx === scopes.length - 1}
                            style={{ background:'none', border:'none', fontSize:12, padding:'2px 4px',
                              color: idx === scopes.length - 1 ? '#ddd' : '#666',
                              cursor: idx === scopes.length - 1 ? 'default' : 'pointer' }}>
                            ▼
                          </button>
                        </div>

                        <button onClick={() => toggleDefault(scope)}
                          title={scope.is_default ? '기본값 해제' : '기본값으로 설정'}
                          style={{ flexShrink:0, fontSize:11, fontWeight:600, whiteSpace:'nowrap',
                            padding:'4px 9px', borderRadius:10, cursor:'pointer',
                            border: scope.is_default ? 'none' : '1px solid #ccc',
                            background: scope.is_default ? '#0F6E56' : '#fff',
                            color: scope.is_default ? '#fff' : '#888' }}>
                          기본값
                        </button>

                        <div style={{ cursor:'pointer', flex:1 }} onClick={() => toggleExpand(scope)}>
                          <span style={{ fontSize:14, fontWeight:600 }}>{scope.name}</span>
                          <span style={{ fontSize:11, color:'#aaa', marginLeft:8 }}>
                            {scope.targets?.length ? `대상 ${scope.targets.length}개 설정됨` : '대상 미설정'}
                          </span>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
                          <button onClick={() => toggleExpand(scope)}
                            style={{ background:'none', border:'none', color:'#0F6E56', fontSize:12,
                              cursor:'pointer', fontWeight:600, whiteSpace:'nowrap' }}>
                            {expandedId === scope.id ? '접기 ▲' : '공개범위 ▼'}
                          </button>
                          <button onClick={() => startEdit(scope)}
                            style={{ background:'none', border:'none', color:'#185FA5', fontSize:12,
                              cursor:'pointer', fontWeight:600 }}>
                            수정
                          </button>
                          <button onClick={() => handleDelete(scope.id, scope.name)}
                            style={{ background:'none', border:'none', color:'#dc2626', fontSize:12,
                              cursor:'pointer', fontWeight:600 }}>
                            삭제
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {expandedId === scope.id && (
                    <div style={{ padding:'4px 14px 16px', background:'#f8fafc' }}>
                      <div style={groupTitle}>회원구분</div>
                      <div style={checkRow}>
                        {MEMBER_TYPE_OPTIONS.map(o => (
                          <label key={o.value} style={checkLabel}>
                            <input type="checkbox" checked={isChecked('member_type', o.value)}
                              onChange={() => toggleTarget('member_type', o.value)}
                              style={{ width:15, height:15, cursor:'pointer' }} />
                            {o.label}
                          </label>
                        ))}
                      </div>

                      <div style={groupTitle}>부서구분</div>
                      <div style={checkRow}>
                        {departments.length === 0 ? (
                          <span style={emptyText}>등록된 부서가 없습니다. (조직구성에서 추가해주세요)</span>
                        ) : departments.map(d => (
                          <label key={d.id} style={checkLabel}>
                            <input type="checkbox" checked={isChecked('department', d.name)}
                              onChange={() => toggleTarget('department', d.name)}
                              style={{ width:15, height:15, cursor:'pointer' }} />
                            {d.name}
                          </label>
                        ))}
                      </div>

                      <div style={groupTitle}>
                        제외할 부서 <span style={{ color:'#aaa', fontWeight:400 }}>(다른 조건과 상관없이 항상 제외)</span>
                      </div>
                      <div style={checkRow}>
                        {departments.length === 0 ? (
                          <span style={emptyText}>등록된 부서가 없습니다. (조직구성에서 추가해주세요)</span>
                        ) : departments.map(d => (
                          <label key={'ex-'+d.id} style={checkLabel}>
                            <input type="checkbox" checked={isChecked('department_exclude', d.name)}
                              onChange={() => toggleTarget('department_exclude', d.name)}
                              style={{ width:15, height:15, cursor:'pointer' }} />
                            {d.name}
                          </label>
                        ))}
                      </div>

                      <div style={groupTitle}>직급구분</div>
                      <div style={checkRow}>
                        {positions.length === 0 ? (
                          <span style={emptyText}>등록된 직급이 없습니다. (조직구성에서 추가해주세요)</span>
                        ) : positions.map(p => (
                          <label key={p.id} style={checkLabel}>
                            <input type="checkbox" checked={isChecked('position', p.name)}
                              onChange={() => toggleTarget('position', p.name)}
                              style={{ width:15, height:15, cursor:'pointer' }} />
                            {p.name}
                          </label>
                        ))}
                      </div>

                      <div style={{ display:'flex', gap:8, marginTop:12 }}>
                        <button onClick={() => saveTargets(scope.id)} disabled={savingTargets}
                          style={{ padding:'8px 16px', background: savingTargets ? '#aaa' : '#0F6E56', color:'#fff',
                            border:'none', borderRadius:8, fontSize:13, fontWeight:600,
                            cursor: savingTargets ? 'not-allowed' : 'pointer' }}>
                          {savingTargets ? '저장 중...' : '저장'}
                        </button>
                        <button onClick={() => setExpandedId(null)}
                          style={{ padding:'8px 16px', background:'#f0f0f0', color:'#555',
                            border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                          취소
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ padding:'0 24px 20px' }}>
          <button onClick={onClose}
            style={{ width:'100%', padding:'11px', background:'#f0f0f0', color:'#555',
              border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

const groupTitle = { fontSize:12, fontWeight:600, color:'#555', margin:'10px 0 6px' };
const checkRow = { display:'flex', flexWrap:'wrap', gap:'6px 16px' };
const checkLabel = { display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' };
const emptyText = { fontSize:12, color:'#aaa' };